import { RADIUS, SPACE, T, TRANSITION } from '@/design/tokens';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';
import { forwardRef } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface BaseProps {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
}

type ButtonAsButton = BaseProps & {
  as?: 'button';
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps>;

type ButtonAsAnchor = BaseProps & {
  as: 'a';
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseProps>;

export type ButtonProps = ButtonAsButton | ButtonAsAnchor;

const SIZE_STYLES: Record<ButtonSize, CSSProperties> = {
  // 44px minimum height enforces the accessibility touch-target rule
  // even on the visually-small 'sm' variant.
  sm: { padding: `${SPACE.xs}px ${SPACE.md}px`, fontSize: 13, minHeight: 44 },
  md: { padding: `${SPACE.sm}px ${SPACE.md}px`, fontSize: 14, minHeight: 44 },
  lg: { padding: `${SPACE.sm}px ${SPACE.lg}px`, fontSize: 16, minHeight: 48 },
};

function variantStyles(variant: ButtonVariant): CSSProperties {
  switch (variant) {
    case 'primary':
      // Use --ap-cta rather than T.accent (--ap-pink): #f3037e gives only 4.10:1 with white.
      // --ap-cta is #c20062 in light (6.05:1) and #cc1c6e in dark (5.30:1) — both AA.
      return { background: 'var(--ap-cta)', color: T.onAccent, border: '1px solid var(--ap-cta)' };
    case 'secondary':
      return { background: 'transparent', color: T.text, border: `1px solid ${T.border}` };
    case 'ghost':
      return { background: 'transparent', color: T.textMuted, border: '1px solid transparent' };
    case 'danger':
      // Use --ap-danger (fixed #c0392b in both themes) rather than T.red (--ap-alert).
      // Dark-mode --ap-alert is lightened to #e0685a for indicators but only gives 3.33:1
      // with white as a fill; --ap-danger #c0392b gives 5.44:1 in both themes (AA ✓).
      return {
        background: 'var(--ap-danger)',
        color: T.onAccent,
        border: '1px solid var(--ap-danger)',
      };
  }
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, ref) {
  const { children, variant = 'primary', size = 'md', loading = false, fullWidth = false } = props;

  const isDisabled = loading || ('disabled' in props && props.disabled === true);

  const baseStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACE.sm,
    borderRadius: RADIUS.md,
    fontWeight: 600,
    letterSpacing: '0.02em',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 0.55 : 1,
    transition: `background ${TRANSITION.fast}, opacity ${TRANSITION.fast}, border-color ${TRANSITION.fast}`,
    width: fullWidth ? '100%' : undefined,
    textDecoration: 'none',
    userSelect: 'none',
    ...SIZE_STYLES[size],
    ...variantStyles(variant),
  };

  if (props.as === 'a') {
    const { as: _as, variant: _v, size: _s, loading: _l, fullWidth: _fw, style, ...rest } = props;
    return (
      <a {...rest} style={{ ...baseStyle, ...style }} aria-disabled={isDisabled || undefined}>
        {children}
      </a>
    );
  }

  const {
    as: _as,
    variant: _v,
    size: _s,
    loading: _l,
    fullWidth: _fw,
    style,
    type,
    ...rest
  } = props;
  return (
    <button
      {...rest}
      ref={ref}
      type={type ?? 'button'}
      style={{ ...baseStyle, ...style }}
      disabled={isDisabled}
      aria-busy={loading || undefined}
    >
      {children}
    </button>
  );
});
